import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import { ARTICLES, CATEGORIES } from "@/data/journal";

export default function Journal() {
  const [cat, setCat] = useState("All");
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const list = cat === "All" ? ARTICLES : ARTICLES.filter((a) => a.category === cat);
  const featured = cat === "All" ? list[0] : null;
  const rest = cat === "All" ? list.slice(1) : list;

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B]">
      <Nav />
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 md:pb-28">
        <div className="max-w-2xl mb-12">
          <div className="overline mb-4">The Journal</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05]">
            Field notes from <span className="italic text-[#AD6A48]">active India.</span>
          </h1>
          <p className="mt-5 text-[#5C665A] leading-relaxed">Travel guides, coffee stories, training blueprints and destination deep-dives — written to be used, not just read.</p>
        </div>

        <div className="flex flex-wrap gap-2 mb-12" data-testid="journal-categories">
          {CATEGORIES.map((c) => (
            <button key={c} data-testid={`journal-cat-${c.toLowerCase().replace(/ /g, "-")}`} onClick={() => setCat(c)}
              className={`rounded-full px-5 py-2.5 text-sm font-medium border transition-colors ${cat === c ? "bg-[#2D372B] border-[#2D372B] text-[#F7F5F0]" : "border-[#E5E2D9] bg-white text-[#5C665A] hover:border-[#89937C]"}`}>
              {c}
            </button>
          ))}
        </div>

        {featured && (
          <Link to={`/journal/${featured.slug}`} data-testid={`article-card-${featured.slug}`} className="group grid lg:grid-cols-2 gap-8 items-center mb-14 bg-white border border-[#E5E2D9] rounded-3xl overflow-hidden hover:border-[#89937C] transition-colors">
            <div className="aspect-[16/10] overflow-hidden">
              <img src={featured.image} alt={featured.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            </div>
            <div className="p-8 lg:pr-12">
              <div className="flex items-center gap-3 text-xs">
                <span className="uppercase tracking-[0.2em] font-semibold text-[#AD6A48]">{featured.category}</span>
                <span className="text-[#5C665A] inline-flex items-center gap-1"><Clock size={11} /> {featured.readTime}</span>
              </div>
              <h2 className="font-serif-display text-3xl lg:text-4xl mt-3 leading-tight">{featured.title}</h2>
              <p className="text-[#5C665A] mt-4 leading-relaxed">{featured.excerpt}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-[#AD6A48] font-medium">Read the story <ArrowUpRight size={15} /></span>
            </div>
          </Link>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {rest.map((a) => (
            <Link key={a.slug} to={`/journal/${a.slug}`} data-testid={`article-card-${a.slug}`} className="group bg-white border border-[#E5E2D9] rounded-3xl overflow-hidden hover:border-[#89937C] transition-colors flex flex-col">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <div className="flex items-center gap-3 text-xs">
                  <span className="uppercase tracking-[0.2em] font-semibold text-[#AD6A48]">{a.category}</span>
                  <span className="text-[#5C665A] inline-flex items-center gap-1"><Clock size={11} /> {a.readTime}</span>
                </div>
                <h3 className="font-serif-display text-xl mt-3 leading-snug flex-1">{a.title}</h3>
                <p className="text-sm text-[#5C665A] mt-2 line-clamp-2">{a.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
}
